import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Autocomplete, InputLabel, Select, MenuItem, FormControl
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { setSuccess } from "../../../Redux/AlertReducer";
import { useDispatch } from "react-redux";
import { Helper } from "../../../tools/Helper";
import { api_Routes } from "../../../tools/api_Routes";

export const FormEditMembership = ({
                                     membershipData,
                                     setMembershipData,
                                     services,
                                     isServicesLoading
                                   }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [selectedServices, setSelectedServices] = useState([]);
  const itemsPerPage = 25;
  const [servicesPage, setServicesPage] = useState(1);
  const [serviceSearchQuery, setServiceSearchQuery] = useState("");

  useEffect(() => {
    if (membershipData?.services && services.length > 0) {
      const initialSelected = services.filter(service =>
          membershipData.services.includes(service.id)
      );
      setSelectedServices(initialSelected);
    }
  }, [membershipData, services]);

  // Pagination and search logic
  const paginatedServices = services.slice(0, servicesPage * itemsPerPage);
  const filteredServices = services.filter((service) =>
      service.title.toLowerCase().includes(serviceSearchQuery.toLowerCase())
  );
  const displayedServices = serviceSearchQuery ? filteredServices : paginatedServices;

  const handleServiceChange = (event, newValue) => {
    setSelectedServices(newValue);
    const serviceIds = newValue.map(service => service.id);
    setMembershipData(prev => ({ ...prev, services: serviceIds }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMembershipData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoadingSubmit(true);

    const formData = new FormData();
    formData.append('name', membershipData.name);
    formData.append('description', membershipData.description);
    formData.append('monthly_price', membershipData.monthly_price);
    formData.append('price_per_item', membershipData.price_per_item);
    formData.append('is_active', membershipData.is_active ? 1 : 0);
    formData.append('allow_multiple_months', membershipData.allow_multiple_months ? 1 : 0);
    formData.append('level', membershipData.level);
    formData.append('discount', membershipData.discount);
    formData.append('discount_type', membershipData.discount_type);

    membershipData.services.forEach(serviceId => {
      formData.append('services[]', serviceId);
    });

    try {
      const { response } = await Helper.Put({
        url: `${api_Routes.Membership.Edit}${id}`,
        data: formData,
        isFormData: true
      });

      if (response) {
        dispatch(setSuccess(true));
        navigate("/Membership");
      }
    } catch (error) {
      console.error("Update error:", error);
      setErrorMessage(error.message);
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  return (
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-4">
          <TextField
              fullWidth
              margin="normal"
              label="Name"
              name="name"
              required
              value={membershipData.name}
              onChange={handleInputChange}
              variant="outlined"
          />

          <TextField
              fullWidth
              margin="normal"
              label="Monthly Price"
              name="monthly_price"
              required
              type="number"
              value={membershipData.monthly_price}
              onChange={handleInputChange}
              variant="outlined"
          />

          <TextField
              fullWidth
              margin="normal"
              label="Price Per Item"
              name="price_per_item"
              required
              type="number"
              value={membershipData.price_per_item}
              onChange={handleInputChange}
              variant="outlined"
          />

          <TextField
              fullWidth
              margin="normal"
              label="Discount"
              name="discount"
              required
              type="number"
              value={membershipData.discount}
              onChange={handleInputChange}
              variant="outlined"
          />
          <FormControl className="w-full">
            <InputLabel id="discount-type-select-label">
              Discount Type
            </InputLabel>
            <Select
                labelId="discount-type-select-label"
                id="discount-type-select"
                value={membershipData.discount_type || null}
                onChange={(e) => {
                  setMembershipData((prev) => ({
                    ...prev,
                    ["discount_type"]: e.target.value,
                  }));
                }}
                label="Discount Type"
                sx={{ textAlign: "start" }}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="const">Constant</MenuItem>
              <MenuItem value="percentage">Percentage</MenuItem>
            </Select>
          </FormControl>

          <div className="w-full">
            <Autocomplete
                multiple
                disablePortal
                loading={isServicesLoading}
                options={displayedServices}
                getOptionLabel={(option) => option.title || ""}
                value={selectedServices}
                onChange={handleServiceChange}
                onInputChange={(event, value) => setServiceSearchQuery(value)}
                renderInput={(params) => (
                    <TextField {...params} label="Services" variant="outlined" />
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    if (listboxNode.scrollTop + listboxNode.clientHeight >=
                        listboxNode.scrollHeight - 3) {
                      setServicesPage(prev => prev + 1);
                    }
                  },
                }}
            />
          </div>

          <TextField
              fullWidth
              margin="normal"
              label="Description"
              name="description"
              multiline
              rows={2}
              value={membershipData.description}
              onChange={handleInputChange}
              variant="outlined"
          />

          <div className="w-full flex items-center justify-start gap-x-3">
            <FormLabel sx={{ fontWeight: "bold" }} component="legend">
              Active:
            </FormLabel>
            <RadioGroup
                name="is_active"
                value={membershipData.is_active.toString()}
                onChange={(e) => {
                  const value = e.target.value === 'true';
                  setMembershipData(prev => ({ ...prev, is_active: value }));
                }}
                row
            >
              <FormControlLabel value="true" control={<Radio />} label="Yes" />
              <FormControlLabel value="false" control={<Radio />} label="No" />
            </RadioGroup>
          </div>

          <div className="w-full flex items-center justify-start gap-x-3">
            <FormLabel sx={{ fontWeight: "bold" }} component="legend">
              Allow Multiple Months:
            </FormLabel>
            <RadioGroup
                name="allow_multiple_months"
                value={membershipData.allow_multiple_months.toString()}
                onChange={(e) => {
                  const value = e.target.value === 'true';
                  setMembershipData(prev => ({ ...prev, allow_multiple_months: value }));
                }}
                row
            >
              <FormControlLabel value="true" control={<Radio />} label="Yes" />
              <FormControlLabel value="false" control={<Radio />} label="No" />
            </RadioGroup>
          </div>
        </div>

        {errorMessage && (
            <div className="w-full text-start text-red-600">*{errorMessage}</div>
        )}

        <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoadingSubmit}
            color="primary"
            sx={{
              display: isLoadingSubmit ? "flex" : "block",
              marginTop: 2,
              width: "20%",
              marginX: "auto",
              fontWeight: "bold",
              justifyContent: "center",
            }}
        >
          {isLoadingSubmit ? (
              <CircularProgress size={24} color="primary" />
          ) : (
              "Update Membership"
          )}
        </Button>
      </form>
  );
};